import { Model } from '@bryntum/schedulerpro';

/**
 * Selected resource record in the shape the map-radius / selection consumers
 * expect. Kept separate from Bryntum's Model type because consumers should
 * not depend on the scheduler's internal record shape.
 */
export interface SelectedItem {
  id: string;
  name: string;
}

/**
 * Convert a Bryntum selection (array of row Models) into the plain
 * { id, name } records the scheduler's onSelectionChange callback forwards
 * to the shell.
 *
 * Resolution order for each field:
 *  - Prefer `model.data.id` / `model.data.name` (the record's underlying data).
 *  - Fall back to `model.id.toString()` for records where the id lives on the
 *    model itself rather than its data (e.g. records created ad-hoc by
 *    Bryntum internals).
 *  - Default to empty strings so the caller never sees `undefined`.
 *
 * Pure function — extracted from SchedulerCore.handleSelectionChange so the
 * selection-mapping logic can be unit-tested without pulling the full
 * BryntumSchedulerPro component into tests.
 */
export function mapSelectionToItems(
  selection: Model[] | undefined | null
): SelectedItem[] {
  if (!selection) return [];
  return selection.map((model) => {
    const modelWithData = model as Model & {
      data?: { id?: string; name?: string };
    };
    return {
      id: modelWithData.data?.id || model.id?.toString() || '',
      name: modelWithData.data?.name || '',
    };
  });
}
